import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { FaUsers, FaProjectDiagram, FaTrophy, FaGlobe } from 'react-icons/fa';

const Stats = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const stats = [
    {
      icon: FaUsers,
      number: 500,
      suffix: '+',
      label: 'Happy Clients',
      description: 'Worldwide satisfied customers',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: FaProjectDiagram,
      number: 1200,
      suffix: '+',
      label: 'Projects Completed',
      description: 'Successfully delivered',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: FaTrophy,
      number: 50,
      suffix: '+',
      label: 'Awards Won',
      description: 'Industry recognition',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: FaGlobe,
      number: 30,
      suffix: '+',
      label: 'Countries Served',
      description: 'Global presence',
      color: 'from-green-500 to-emerald-500',
    },
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Animated Grid */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-40" />
        
        {/* Gradient Orbs */}
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500 rounded-full blur-[150px]"
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, delay: 2 }}
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500 rounded-full blur-[150px]"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-full text-sm font-semibold mb-4"
          >
            Our Achievements
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Numbers That 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400"> Speak Volumes</span>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Our track record of success speaks for itself. Here are some milestones we've achieved together with our clients.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div ref={ref} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="relative group"
            >
              {/* Card */}
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 h-full hover:bg-white/20 transition-all duration-300">
                {/* Gradient Glow on Hover */}
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${stat.color} opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-xl`} />
                
                <div className="relative">
                  {/* Icon */}
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${stat.color} flex items-center justify-center mb-6 shadow-lg`}
                  >
                    <stat.icon className="text-white text-2xl" />
                  </motion.div>

                  {/* Number Counter */}
                  <div className="text-5xl font-bold text-white mb-2">
                    {inView ? (
                      <CountUp
                        start={0}
                        end={stat.number}
                        duration={2.5}
                        separator=","
                      />
                    ) : (
                      '0'
                    )}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                      {stat.suffix}
                    </span>
                  </div>

                  {/* Label */}
                  <h3 className="text-xl font-semibold text-white mb-2">{stat.label}</h3>
                  <p className="text-white/60">{stat.description}</p>
                </div>

                {/* Decorative Corner */}
                <div className="absolute top-4 right-4 w-20 h-20">
                  <svg viewBox="0 0 100 100" className="w-full h-full opacity-10">
                    <circle cx="80" cy="20" r="15" fill="white" />
                    <circle cx="50" cy="50" r="8" fill="white" />
                    <circle cx="20" cy="80" r="5" fill="white" />
                  </svg>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-16 overflow-hidden"
        >
          <div className="flex animate-marquee whitespace-nowrap">
            {[...Array(2)].map((_, setIndex) => (
              <div key={setIndex} className="flex items-center gap-8 mx-4">
                {['Google Partner', 'Meta Partner', 'HubSpot Certified', 'Semrush Partner', 'Shopify Expert', 'AWS Partner'].map((partner, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-6 py-3 bg-white/5 backdrop-blur-sm rounded-full border border-white/10"
                  >
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-400" />
                    <span className="text-white/70 font-medium">{partner}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Stats;
